import { Link } from '@fluentui/react';
import { useTelemetry } from '@iamexperiences/ecos-telemetry';
import { AuthPrompt, useAuth } from '@iamexperiences/feature-auth';
import {
  FlexPanelCollections,
  IConfiguration,
  IHeaderActionProps,
  ILocalizationMap,
  IOrganization
} from '@iamexperiences/suite-header';
import { ITenantProps } from '@iamexperiences/suite-header/lib-commonjs/SuiteHeader/TenantList/ITenantProps';
import i18next from 'i18next';
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Constants } from '../../shared/Constants/Constants';
import { settingsManager } from '../../shared/settings';
import { AppsActions } from '../../shared/Telemetry/AppsActions';
import { useLocalization } from '../atoms/useLocalization';
import {
  useGetCurrentOrganization,
  useGetOfficeShellData,
  useGetOrganizationBranding,
  useGetUserImage,
} from '../client-header-tenant';
import { NavBarWrapper } from './Header.types';
import { LazyLoadingHeader } from './LazyLoadingHeader';
import { useHeaderOrganizations } from '../client-header-tenant/molecules/userHeaderOrganizations';
import { isFeatureEnabled } from '../../shared/SettingsManager';

const SuiteHeader = lazy(async () =>
  await import(/* webpackPrefetch: true, webpackChunkName: "ecos-header" */ '@iamexperiences/suite-header/lib/SuiteHeader').then((module) => ({ default: module.SuiteHeader })));


const DEFAULT_HELPLINK =
  'https://docs.microsoft.com/en-us/azure/active-directory/user-help/my-apps-portal-end-user-access';
const DEFAULT_EVO_URL = 'https://login.microsoftonline.com';

const dataCacheKeys = ['tileLibrary', 'collectionOrder', 'collectionLibrary'];

interface HeaderProps {
  extraActions?: IHeaderActionProps[];
}

export const Header: React.FC<HeaderProps> = ({ extraActions }) => {
  const { auth } = useAuth();
  const user = auth.user;
  const clientId = auth.clientId;
  const tenantId = user?.tenantId;
  const userId = user?.objectId;
  const username = user?.username;
  const name = user?.name;
  const claims = user && (user.rawClaims as { xms_pl?: unknown } | undefined);
  const [language, setLanguage] = useState<string | undefined>(
    claims && typeof claims.xms_pl === 'string' ? claims.xms_pl : undefined
  );
  const helpLink = settingsManager.get('panelHelpLink') ?? DEFAULT_HELPLINK;
  const evoBase = settingsManager.get('azureInstance') ?? DEFAULT_EVO_URL;
  const msGraphBaseUrl = settingsManager.getRequired('msGraphResourceName');
  const viewAccountLink = settingsManager.get('meControlViewAccountLink');
  const isOfficeAppLauncherDisabled = settingsManager.get('isOfficeAppLauncherDisabled');
  const isUniversalMeControlEnabled = isFeatureEnabled(settingsManager.get('isUniversalMeControlEnabled'), tenantId);

  const telemetry = useTelemetry();
  const { data: currentOrganization, isLoading: isCurrentOrganizationLoading } = useGetCurrentOrganization(tenantId ?? '');
  const { data: branding } = useGetOrganizationBranding(tenantId ?? '');
  const { data: userImageUrl, isLoading: isUserImageLoading } = useGetUserImage(userId ?? '');
  const { data: officeShellData } = useGetOfficeShellData(tenantId ?? '');
  const isGuestUser = useMemo(() => user?.homeTenantId !== user?.tenantId, [user]);

  const bannerLogo = useMemo(() => {
    if (branding && branding.cdnList?.length > 0 && branding?.bannerLogoRelativeUrl) {
      return `https://${branding.cdnList[0]}/${branding.bannerLogoRelativeUrl}`;
    } else {
      return undefined;
    }
  }, [branding]);

  const [t] = useLocalization();
  const localizationMap: ILocalizationMap = useMemo(() => ({
    apps: t('Apps'),
    accessibilityLink: t('frenchAccessibilityLinkText'),
    switchOrgIntroText: t('switchOrgIntroText'),
    currentOrganizationTitle: t('currentOrganizationTitle'),
    otherOrganizationsTitle: t('otherOrganizationsTitle'),
    manageOrganizations: t('manageOrganizationsLinkText'),
    closePanelAriaLabel: t('closeButtonAriaLabel'),
    tenantBrandingImageAlt: t('tenantBranding', { displayName: currentOrganization?.displayName }),
    organizations: t('organizations'),
    loadingOrganizations: t('loadingOrganizations'),
    organizationsEmpty: t('emptyOrganizations'),
    finishLoading: t('loaded'),
    organizationError: t('getOrganizationsError'),
    helpPanelHeaderText: t('help'),
    viewApps: t('viewApps'),
    helpPanelTermsOfUseLink: t('termsOfUse'),
    helpPanelPrivacyLink: t('privacy'),
    accountManager: t('accountManager'),
    switchOrganization: t('switchOrganization'),
    moreOptions: t('more'),
    leaveNewExp: t('leaveNewExp'),
    waffleNotAvailableHeader: t('fallbackWaffleHeaderText'),
    waffleNotAvailableDescription: t('fallbackWaffleMainText'),
    viewMyAccount: t('viewMyAccount'),
    signOut: t('signOut'),
    openInNewTab: t('openInNewTab')
  }), [t, currentOrganization?.displayName]);

  useEffect(() => {
    const lng = i18next.languages[0];
    setLanguage(lng);
  }, [i18next.languages[0]]);

  const changeTenant = useCallback((targetTenant: ITenantProps, currentTenant?: IOrganization) => {
    telemetry.reportCustomEvent(AppsActions.switchOrg, {
      source: currentTenant?.displayName,
      target: targetTenant.displayName
    });
    auth.login({ tenantId: targetTenant.tenantId, loginHint: auth.user?.username });
  }, [auth, telemetry]);

  const switchAccount = useCallback(() => {
    auth.login({ prompt: AuthPrompt.SELECT_ACCOUNT });
  }, [auth]);

  const switchTo = useCallback((upn: string) => {
    auth.login({ loginHint: upn });
  }, [auth]);

  const handleSignIn = useCallback(() => {
    auth.login({});
  }, [auth]);

  const handleHeaderRender = useCallback(() => {
    telemetry.reportCustomEvent(AppsActions.headerRendered);
  }, [telemetry]);

  const manageOrganizationControl = useCallback(() => {
    const organizationLink = new URL(`${viewAccountLink ?? ''}/organizations`);
    organizationLink.searchParams.set(Constants.TENANT_ID_QUERY_STRING, tenantId ?? '');
    organizationLink.searchParams.set(Constants.UPN_QUERY_STRING, username ?? '');
    return (
      <Link href={organizationLink.toString()} target="_blank">
        {t('manageOrganizationsLinkText')}
      </Link>
    );
  }, [viewAccountLink, tenantId, username, t]);

  const userData = useMemo(() => {
    return {
      tenantId: tenantId ?? '',
      tenantDisplayName: currentOrganization?.displayName ?? '',
      objectId: userId ?? '',
      upn: username ?? '',
      name: name ?? '',
      imageUrl: userImageUrl,
      rawClaims: user?.rawClaims ?? {},
      homeTenantId: user?.homeTenantId ?? ''
    };
  }, [currentOrganization?.displayName, name, tenantId, userId, userImageUrl, username]);

  const getToken = useCallback(async (baseUrl: string) => {
    return await auth.acquireToken({ scopes: [baseUrl] });
  }, [auth]);
  const configuration: IConfiguration = useMemo(() => {
    return {
      msGraphBaseUrl: msGraphBaseUrl,
      getToken: getToken
    }
  }, [msGraphBaseUrl, getToken]);
  const {
    data: organizations,
    isLoading: organizationsLoading,
    isError: isErrorGettingOrganizations
  } = useHeaderOrganizations(configuration, userData);

  const organizationsProps = useMemo(
    () => ({ value: organizations ?? [], isLoading: organizationsLoading }),
    [organizations, organizationsLoading]
  );

  // Keep the latest organizations data in refs so callbacks (e.g. logOut) can read
  // current values without depending on them. Without this, logOut's identity changes
  // when tenant data resolves, which causes suite-header's MeControl useEffect to re-run
  // and re-call the /authorize endpoint every time the data settles.
  const organizationsRef = useRef(organizations);
  const organizationsLoadingRef = useRef(organizationsLoading);
  useEffect(() => {
    organizationsRef.current = organizations;
    organizationsLoadingRef.current = organizationsLoading;
  }, [organizations, organizationsLoading]);

  const queryClient = useQueryClient();
  const shouldRefreshTenantInfo = useRef(false);

  const handleHeaderButtonClick = useCallback((buttonId: string) => {
    // refetch tenant info when opening tenant switching panel and tenant data finished loading
    if (buttonId === FlexPanelCollections.organizations && !organizationsLoading) {
      // don't refetch tenant info when opening the panel for the first time, but do refetch
      // on all subsequent panel openings
      if (!shouldRefreshTenantInfo.current) {
        shouldRefreshTenantInfo.current = true;
      } else {
        queryClient.invalidateQueries(['getTenantsInfo']);
      }
    }
    telemetry.reportCustomEvent(AppsActions.headerButtonClicked, { buttonId });
  }, [telemetry, queryClient, organizationsLoading]);

  const logOut = useCallback(() => {
    const currentOrganizations = organizationsRef.current;
    const currentOrganizationsLoading = organizationsLoadingRef.current;
    // Iterate through local storage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      // Process only when the key is not null
      if (!key) continue;
      dataCacheKeys.forEach(dataKey => {
        // If tenants are still loading, remove all data with relevant keys
        if (currentOrganizationsLoading && key.includes(dataKey)) {
          localStorage.removeItem(key);
          return;
        }
        // If organizations have loaded, remove data related to the current users tenants
        currentOrganizations?.forEach(org => {
          if (key.includes(dataKey) && key.includes(org.tenantId)) {
            localStorage.removeItem(key);
          }
        });
      });
    }
    telemetry.reportCustomEvent(AppsActions.meControlLogout);
    telemetry.destroy();
    void auth.logout();
  }, [telemetry, auth]);


  if (language === undefined) {
    return null;
  }

  // Gate the SuiteHeader render until the async data that feeds `userData` (tenant
  // display name + profile photo) has settled. This lets suite-header's MeControl
  // initialize once with a stable userData object, instead of re-running its load
  // effect — and re-calling the /authorize endpoint — each time one of these fields
  // resolves after first paint. Only applies to signed-in users so the signed-out
  // header isn't blocked.
  if (userId && (isCurrentOrganizationLoading || isUserImageLoading)) {
    return <LazyLoadingHeader />;
  }

  return (
    <Suspense fallback={<LazyLoadingHeader></LazyLoadingHeader>}>
      <SuiteHeader
        appId={clientId}
        enabledApps={[]}
        organizations={organizationsProps}
        changeTenant={changeTenant}
        culture={language}
        evoBase={evoBase}
        extraActions={extraActions}
        hasError={isErrorGettingOrganizations}
        helpLink={helpLink}
        imageUrl={bannerLogo}
        isOfficeAppLauncherDisabled={isOfficeAppLauncherDisabled || isGuestUser}
        isUniversalMeControlEnabled={isUniversalMeControlEnabled}
        localizationMap={localizationMap}
        navbarData={officeShellData as NavBarWrapper}
        onRenderComplete={handleHeaderRender}
        onHeaderButtonClicked={handleHeaderButtonClick}
        manageTenantControl={manageOrganizationControl}
        isMsalJsEnabled={true}
        onSignIn={handleSignIn}
        onSignOut={logOut}
        onSwitch={switchAccount}
        onSwitchTo={switchTo}
        theme={undefined}
        userData={userData}
        viewAccountLink={viewAccountLink ?? ''}
        workloadId="AADMyApps"
      />
    </Suspense>
  );
};
